/**
 * Game Start Sequencer for Poker Reborn
 *
 * Orchestrates the animation sequence when a new hand starts.
 *
 * Flow (per GAME_START_ANIMATION.md):
 *   START-1: Reset table (clear previous hand)
 *   postResetPause →
 *   START-2: Blinds animation (future: animate blind chips)
 *   postBlindsPause →
 *   START-3: Round-robin card dealing
 *   postDealPause
 *
 * @see docs/systems/animations/GAME_START_ANIMATION.md - Full specification
 * @see docs/systems/animations/GAME_START_SEQUENCER_PLAN.md - Design plan
 */

import { log } from '@/utils/log';
import { debugOverlay } from '@/utils/DebugOverlay';
import { GAME_START_TIMING } from '@/config/animation';
import { DEALING_CONFIG } from '@/config/dealingAnimation';
import { stageManager } from '@/effects/stage';
import { tableState } from '@/state/TableState';
import type { Card } from '@/types/game';
import type { BlindsData } from './GameStartEventCollector';

// =============================================================================
// TYPES
// =============================================================================

export interface GameStartData {
  /** Game number */
  gameNumber: number;
  /** Dealer seat */
  dealerSeat: number;
  /** Blinds data */
  blinds: BlindsData;
  /** Hero's hole cards (null for spectators) */
  heroCards: Card[] | null;
  /** Whether hero is spectating */
  isSpectator: boolean;
}

// =============================================================================
// GAME START SEQUENCER CLASS
// =============================================================================

/**
 * Orchestrates the game-start animation sequence.
 *
 * This class manages the animation flow when a hand starts:
 * - Reset table state
 * - Post blinds
 * - Deal cards round-robin
 *
 * Usage:
 * ```typescript
 * gameStartSequencer.playSequence(data);
 * ```
 *
 * The sequence is fire-and-forget - callers should NOT await it.
 */
export class GameStartSequencer {
  private static _instance: GameStartSequencer | null = null;

  /** Track if a sequence is currently playing */
  private _isPlaying = false;

  // ---------------------------------------------------------------------------
  // Constructor & Singleton
  // ---------------------------------------------------------------------------

  private constructor() {}

  /**
   * Get the singleton instance.
   */
  static getInstance(): GameStartSequencer {
    if (!GameStartSequencer._instance) {
      GameStartSequencer._instance = new GameStartSequencer();
    }
    return GameStartSequencer._instance;
  }

  /**
   * Reset the singleton instance (for testing).
   */
  static resetInstance(): void {
    GameStartSequencer._instance = null;
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  /**
   * Check if a sequence is currently playing.
   */
  get isPlaying(): boolean {
    return this._isPlaying;
  }

  /**
   * Execute the game-start animation sequence.
   *
   * This is fire-and-forget: callers should NOT await this.
   * The promise is returned for testing purposes only.
   *
   * @param data - The collected game start data
   * @returns Promise that resolves when the sequence completes
   */
  async playSequence(data: GameStartData): Promise<void> {
    if (this._isPlaying) {
      log.effects.warning`GameStartSequencer: sequence already playing, skipping`;
      debugOverlay.log(`[GAME_START] already playing, SKIPPED!`);
      return;
    }

    this._isPlaying = true;
    const startTime = Date.now();
    const elapsed = () => `+${Date.now() - startTime}ms`;

    // =========================================================================
    // HEADER
    // =========================================================================
    debugOverlay.log(`----------------------------------------`);
    debugOverlay.log(`[GAME_START] SEQUENCE START`);
    debugOverlay.log(`----------------------------------------`);
    debugOverlay.log(`  game: ${data.gameNumber}`);
    debugOverlay.log(`  dealer: seat ${data.dealerSeat}`);
    debugOverlay.log(`  spectator: ${data.isSpectator}`);
    debugOverlay.log(`  heroCards: ${data.heroCards ? 'yes' : 'no'}`);

    log.effects.info`GameStartSequencer: starting (game ${data.gameNumber}, spectator: ${data.isSpectator})`;

    try {
      // =========================================================================
      // START-1: Reset Table
      // =========================================================================
      debugOverlay.log(`${elapsed()} [START-1] Reset table`);
      this.resetTable();
      debugOverlay.log(`${elapsed()} [START-1] done`);

      // postResetPause
      if (GAME_START_TIMING.postResetPause > 0) {
        debugOverlay.log(`${elapsed()} pause: ${GAME_START_TIMING.postResetPause}ms`);
        await this.delay(GAME_START_TIMING.postResetPause);
      }

      // =========================================================================
      // START-2: Blinds Animation
      // =========================================================================
      debugOverlay.log(`${elapsed()} [START-2] Blinds (SB: seat ${data.blinds.smallBlind.seat}, BB: seat ${data.blinds.bigBlind.seat})`);
      // Blinds animation is already running (triggered by blinds:posted event)
      this.handleBlindsStep(data.blinds);
      debugOverlay.log(`${elapsed()} [START-2] done`);

      // postBlindsPause
      if (GAME_START_TIMING.postBlindsPause > 0) {
        debugOverlay.log(`${elapsed()} pause: ${GAME_START_TIMING.postBlindsPause}ms`);
        await this.delay(GAME_START_TIMING.postBlindsPause);
      }

      // =========================================================================
      // START-3: Deal Cards (Round-Robin)
      // =========================================================================
      debugOverlay.log(`${elapsed()} [START-3] Deal cards (dealer: seat ${data.dealerSeat})`);
      await this.dealCards(data.dealerSeat, data.heroCards, data.isSpectator);
      debugOverlay.log(`${elapsed()} [START-3] done`);

      // postDealPause
      if (GAME_START_TIMING.postDealPause > 0) {
        debugOverlay.log(`${elapsed()} pause: ${GAME_START_TIMING.postDealPause}ms`);
        await this.delay(GAME_START_TIMING.postDealPause);
      }

      // =========================================================================
      // COMPLETE
      // =========================================================================
      debugOverlay.log(`----------------------------------------`);
      debugOverlay.log(`[GAME_START] COMPLETE (total: ${elapsed()})`);
      debugOverlay.log(`----------------------------------------`);
      log.effects.info`GameStartSequencer: sequence complete`;

    } catch (error) {
      debugOverlay.log(`[GAME_START] ERROR: ${error}`);
      log.effects.error`GameStartSequencer: error during sequence: ${error}`;
    } finally {
      this._isPlaying = false;
    }
  }

  // ---------------------------------------------------------------------------
  // START-1: Reset Table
  // ---------------------------------------------------------------------------

  /**
   * Reset table state via StageManager.
   * Calls TableScreen.resetAllCards() + gamePad.hideShowMuckButtons()
   */
  private resetTable(): void {
    stageManager.resetTable();
    log.effects.debug`GameStartSequencer: reset table via StageManager`;
  }

  // ---------------------------------------------------------------------------
  // START-2: Blinds Animation
  // ---------------------------------------------------------------------------

  /**
   * Handle blinds animation.
   * Currently blinds are animated immediately when posted (via blinds:posted event).
   * This step is kept for timing/sequencing purposes.
   */
  private handleBlindsStep(_blinds: BlindsData): void {
    // Blinds animation is already triggered by blinds:posted event in TableScreen
    // which fires when the server sends blinds info. This happens before the
    // sequencer runs, so we don't need to do anything here.
    log.effects.debug`GameStartSequencer: blinds step (animation already running)`;
  }

  // ---------------------------------------------------------------------------
  // START-3: Deal Cards
  // ---------------------------------------------------------------------------

  /**
   * Start dealing sequence via StageManager and wait for completion.
   */
  private async dealCards(
    dealerSeat: number,
    _heroCards: Card[] | null,
    _isSpectator: boolean
  ): Promise<void> {
    // Start the dealing sequence via StageManager
    stageManager.startDealing(dealerSeat);
    log.effects.debug`GameStartSequencer: started dealing via StageManager`;

    // Calculate total dealing time based on DEALING_CONFIG
    // Each round: playerCount * cardStaggerDelay, with roundGapDelay between rounds
    // We estimate based on max players (9) for safety
    const maxPlayers = 9;
    const holeCardCount = tableState.holeCardCount.get();
    const { cardStaggerDelay, roundGapDelay } = DEALING_CONFIG;
    const estimatedDealTime =
      holeCardCount * maxPlayers * cardStaggerDelay + // All rounds
      (holeCardCount - 1) * roundGapDelay +           // Gaps between rounds
      200;                                             // Buffer for animation completion

    // Wait for dealing to complete
    await this.delay(estimatedDealTime);
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  /**
   * Delay helper.
   */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}

// =============================================================================
// SINGLETON EXPORT
// =============================================================================

/**
 * Global game start sequencer instance.
 */
export const gameStartSequencer = GameStartSequencer.getInstance();
